import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { CheckoutProgress, CheckoutProgressStep } from "../../utils/interfaces";
import { getCheckoutProgressIndex, getCheckoutProgressSteps } from "../../utils/checkoutProgress";

type Props = {
  progress?: CheckoutProgress;
  fallbackStep?: CheckoutProgressStep;
  method?: CheckoutProgress["method"];
};

export function CheckoutProgressPanel({ progress, fallbackStep = "waiting_payment", method = "desconhecido" }: Props) {
  const currentStep = progress?.step ?? fallbackStep;
  const effectiveMethod = method;
  const steps = getCheckoutProgressSteps(effectiveMethod);
  const activeIndex = getCheckoutProgressIndex(effectiveMethod, currentStep);
  const isFailed = progress?.status === "failed" || currentStep === "failed";
  const isCompleted = progress?.status === "completed" || currentStep === "completed";
  const failedIndex = activeIndex > 0 ? Math.min(activeIndex, steps.length - 1) : 0;
  const minTrackWidth = steps.length >= 7 ? "920px" : "760px";

  return (
    <Box
      w="100%"
      bg="white"
      border="1px solid"
      borderColor={isFailed ? "red.200" : "purple.100"}
      borderRadius="2xl"
      p={6}
      boxShadow="sm"
    >
      <Box overflowX="auto" pb={2}>
        <Flex align="center" justify="space-between" gap={4} mb={4} minW={minTrackWidth}>
          {steps.map((step, index) => {
            const isDone = isCompleted || (!isFailed && index < activeIndex);
            const isActive = !isCompleted && !isFailed && index === activeIndex;
            const isCurrentFailed = isFailed && index === failedIndex;
            const circleBg = isCurrentFailed ? "red.500" : isDone ? "green.500" : isActive ? "purple.700" : "gray.300";
            const lineBg = isDone ? "green.400" : index < activeIndex && !isFailed ? "purple.300" : "gray.200";

            return (
              <Flex key={step.key} align="center" flex="1" minW={0}>
                <Flex direction="column" align="center" flex="1" minW={0}>
                  <Flex
                    w="46px"
                    h="46px"
                    borderRadius="full"
                    bg={circleBg}
                    color="white"
                    align="center"
                    justify="center"
                    fontWeight="bold"
                    boxShadow={isActive ? "0 0 0 8px rgba(83, 7, 115, 0.10)" : "none"}
                    flexShrink={0}
                  >
                    {isActive ? <Spinner size="sm" thickness="3px" /> : isDone ? "✓" : index + 1}
                  </Flex>
                  <Text
                    mt={3}
                    fontSize="sm"
                    textAlign="center"
                    fontWeight={isActive || isCurrentFailed || isDone ? "700" : "500"}
                    color={isCurrentFailed ? "red.600" : isDone ? "green.600" : isActive ? "purple.800" : "gray.500"}
                    lineHeight="1.35"
                    minH="42px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {step.label}
                  </Text>
                </Flex>
                {index < steps.length - 1 && (
                  <Box
                    flex="1"
                    h="3px"
                    bg={lineBg}
                    borderRadius="full"
                    mt="-30px"
                    minW="24px"
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      </Box>

      <Box
        mt={5}
        px={4}
        py={3}
        borderRadius="xl"
        bg={isFailed ? "red.50" : isCompleted ? "green.50" : "purple.50"}
      >
        <Text
          fontWeight="700"
          color={isFailed ? "red.700" : isCompleted ? "green.700" : "purple.800"}
        >
          {progress?.label ?? steps[activeIndex]?.label ?? "Aguardando pagamento"}
        </Text>
        <Text mt={1} fontSize="sm" color={isFailed ? "red.600" : "gray.700"}>
          {progress?.description ?? "Estamos acompanhando o andamento do pagamento e da finalização do pedido."}
        </Text>
      </Box>
    </Box>
  );
}

export function CheckoutStayOnPageWarning() {
  return (
    <Box
      w="100%"
      bg="orange.50"
      border="1px solid"
      borderColor="orange.200"
      borderRadius="xl"
      px={{ base: 4, md: 5 }}
      py={4}
    >
      <Text fontWeight="800" color="orange.700">
        Atenção: mantenha esta tela aberta até o pedido ser finalizado.
      </Text>
      <Text mt={1} fontSize="sm" color="orange.800">
        A confirmação pode levar alguns minutos. Se você trocar de aba, fechar ou navegar para outra tela durante o processamento, aguarde ao retornar para que o sistema atualize o status do pedido.
      </Text>
    </Box>
  );
}
